import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore } from '@/lib/auth/auth-store';
import type { BellConnection } from '../types';
import { joinBell } from './bell-leader';
import { burstDebounce, POLL_INTERVAL_MS } from './bell-stream';
import { notificationKeys } from './notifications-api';

/** Nudges in one burst refetch once (AC7.4). */
const NUDGE_DEBOUNCE_MS = 1_000;
/** ...plus a random 0–2 s, so every tab of a large tenant does not refetch at once. */
export const NUDGE_JITTER_MS = 2_000;
/**
 * The footer does not say "Reconnecting" for a stream that is simply still
 * opening: a normal connect reaches `ready` well inside this. Past it, a
 * stream that has not said `ready` is shown (and polled) as down.
 */
export const CONNECT_GRACE_MS = 2_000;

/** `connecting`: joined, but the stream has not said `ready` yet. */
type StreamState = BellConnection | 'connecting';

export interface UseBellStreamOptions {
  /** A uniform draw in [0, 1); tests inject it. */
  random?: () => number;
}

/**
 * Keep this tab's bell live (R7). Joins the browser's one stream (the leader
 * tab holds it; see `joinBell`), refetches on every relayed nudge, polls every
 * 5 minutes while the stream is down, and catches up once when it returns.
 *
 * Returns what the footer shows: `live` once the stream has said `ready`, or
 * `reconnecting` while it is down, refused, or has not said `ready` within
 * `CONNECT_GRACE_MS` (AC2.5). Inside that grace the footer stays on `live`
 * rather than flash "Reconnecting" on every fast connect; nothing polls then. Mount it only where the bell renders: an applicant
 * or a tenantless staff member never joins (AC1.1), and unmounting on
 * sign-out aborts the stream and releases leadership.
 */
export function useBellStream(options: UseBellStreamOptions = {}): BellConnection {
  // A test seam, fixed for the hook's life so the debounce is not rebuilt.
  const random = useRef(options.random ?? Math.random).current;
  const qc = useQueryClient();
  const accountId = useAuthStore((s) => s.user?.accountId ?? null);
  const tenantId = useAuthStore((s) => s.user?.tenantId ?? null);
  const isApplicant = useAuthStore((s) => s.user?.kind === 'APPLICANT');
  const [status, setStatus] = useState<StreamState>('connecting');
  const previous = useRef<StreamState>('connecting');

  const refetch = useMemo(
    () => burstDebounce(() => void qc.invalidateQueries({ queryKey: notificationKeys.all }), NUDGE_DEBOUNCE_MS, NUDGE_JITTER_MS, random),
    [qc, random],
  );

  useEffect(() => {
    if (!accountId || !tenantId || isApplicant) return;
    const bell = joinBell({ accountId, tenantId, onChanged: refetch, onStatus: setStatus });
    const grace = setTimeout(() => setStatus((s) => (s === 'connecting' ? 'reconnecting' : s)), CONNECT_GRACE_MS);
    return () => {
      clearTimeout(grace);
      bell.leave();
      refetch.cancel();
      setStatus('connecting');
    };
  }, [accountId, tenantId, isApplicant, refetch]);

  useEffect(() => {
    const was = previous.current;
    previous.current = status;
    if (status === 'connecting') return;
    if (status === 'live') {
      // Nudges sent while the stream was down were lost: read once to catch up.
      if (was === 'reconnecting') void qc.invalidateQueries({ queryKey: notificationKeys.all });
      return;
    }
    const poll = setInterval(() => void qc.invalidateQueries({ queryKey: notificationKeys.all }), POLL_INTERVAL_MS);
    return () => clearInterval(poll);
  }, [status, qc]);

  return status === 'connecting' ? 'live' : status;
}
